import { useRef } from "react";
import { toast } from "sonner";
import { GRID_SIZE } from "../../lib/engine";
import { occupied as occupiedBy, buildingIndexAt as indexAt, dimsOf } from "../../lib/editorGeom";

// Masukan mouse kanvas editor: klik (bangun/hapus/pindah/putar/inspector), seret-gambar jalan/sungai/landasan,
// drag-pan, roda zoom, klik kanan hapus tile. Mengembalikan handler untuk <canvas> + resetDraw (ganti tool).
export function useEditorInput({ state, setState, defs, mode, tool, toolVid, moving, setMoving, setSel, setHover, ghost, roadWidth, eraseSize = 1, commit, engineRef, canvasRef, undoRef, doRender, rotateBuildingAt }) {
  const dragRef = useRef(null);
  const movedRef = useRef(false);   // true jika mouse bergerak (drag-pan) sejak mousedown
  const drawingRef = useRef(false);
  const occupied = (list, gx, gy, w, h, ex) => occupiedBy(defs, list, gx, gy, w, h, ex);
  const buildingIndexAt = (gx, gy) => indexAt(defs, state.buildings, gx, gy);

  function gridAt(e) {
    // offsetX/offsetY lebih stabil untuk canvas absolute; fallback ke clientX-rect bila tidak ada.
    if (e.nativeEvent && typeof e.nativeEvent.offsetX === "number") {
      return engineRef.current.toGrid(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    }
    const rect = canvasRef.current.getBoundingClientRect();
    return engineRef.current.toGrid(e.clientX - rect.left, e.clientY - rect.top);
  }

  function setTile(kind, gx, gy, action) {
    if (gx < 0 || gy < 0 || gx >= GRID_SIZE || gy >= GRID_SIZE) return;
    const key = `${gx},${gy}`;
    setState(prev => {
      const set = new Set(prev[kind] || []);
      if (action === "add") { if (set.has(key)) return prev; set.add(key); }
      else { if (!set.has(key)) return prev; set.delete(key); }
      return { ...prev, [kind]: [...set] };
    });
  }

  function paintRoad(gx, gy, action, perp) {
    setTile("roads", gx, gy, action);
    // lajur tambahan tegak lurus arah seretan (gang 1 / jalan raya 2 / protokol 4)
    if (!perp) return;
    for (let i = 1; i < roadWidth; i++) setTile("roads", gx + perp[0] * i, gy + perp[1] * i, action);
  }

  // Landasan pacu: selalu lebar 2 tile tegak lurus arah seretan (pita lebar)
  function paintRunway(gx, gy, action, perp) {
    setTile("runways", gx, gy, action);
    if (perp) setTile("runways", gx + perp[0], gy + perp[1], action);
  }

  // Lukis satu sel jalan/sungai/landasan dalam stroke aktif
  function paintCell(gx, gy) {
    const d = drawingRef.current;
    if (!d) return;
    if (d.kind === "roads") paintRoad(gx, gy, d.action, d.perp);
    else if (d.kind === "runways") paintRunway(gx, gy, d.action, d.perp);
    else setTile("water", gx, gy, d.action);
  }

  function onClick(e) {
    if (!state) return;
    if (movedRef.current) { movedRef.current = false; return; } // habis drag-pan, bukan klik
    if (mode === "road" || mode === "water" || mode === "runway") return; // sudah ditangani drag-draw di mousedown
    const [gx, gy] = gridAt(e);

    if (mode === "pan") return;

    if (mode === "erase" && eraseSize > 1) {
      // Hapus area N×N berjangkar di petak klik: semua gedung yang tapaknya bersinggungan + jalan/sungai/landasan di dalamnya
      const x0 = gx, y0 = gy, x1 = gx + eraseSize, y1 = gy + eraseSize;
      const dalam = (k) => { const [x, y] = k.split(",").map(Number); return x >= x0 && x < x1 && y >= y0 && y < y1; };
      const sisaBangunan = state.buildings.filter(b => { const [w, h] = dimsOf(defs, b); return b.x + w <= x0 || b.x >= x1 || b.y + h <= y0 || b.y >= y1; });
      const next = { ...state, buildings: sisaBangunan, roads: (state.roads || []).filter(k => !dalam(k)), water: (state.water || []).filter(k => !dalam(k)), runways: (state.runways || []).filter(k => !dalam(k)) };
      const nB = state.buildings.length - sisaBangunan.length, nT = ((state.roads || []).length - next.roads.length) + ((state.water || []).length - next.water.length) + ((state.runways || []).length - next.runways.length);
      if (!nB && !nT) { toast.message("Area kosong, tidak ada yang dihapus"); return; }
      commit(next);
      toast.success(`Area ${eraseSize}×${eraseSize} dibersihkan: ${nB} bangunan, ${nT} petak jalan/air`);
      return;
    }
    if (mode === "erase") {
      const idx = buildingIndexAt(gx, gy);
      if (idx >= 0) {
        const next = { ...state, buildings: state.buildings.filter((_, i) => i !== idx) };
        commit(next);
        toast.success("Bangunan dihapus");
        return;
      }
      const key = `${gx},${gy}`;
      if ((state.roads || []).includes(key)) {
        commit({ ...state, roads: (state.roads || []).filter(k => k !== key) });
        toast.success("Jalan dihapus");
        return;
      }
      if ((state.runways || []).includes(key)) {
        commit({ ...state, runways: (state.runways || []).filter(k => k !== key) });
        toast.success("Landasan dihapus");
        return;
      }
      if ((state.water || []).includes(key)) {
        commit({ ...state, water: (state.water || []).filter(k => k !== key) });
        toast.success("Sungai dihapus");
        return;
      }
      return;
    }
    if (mode === "move") {
      // Klik gedung → angkat; klik petak tujuan → letakkan
      if (moving !== null) {
        if (!ghost || !ghost.valid) { toast.error("Lahan tidak valid untuk dipindah ke sini"); return; }
        const next = { ...state, buildings: [...state.buildings] };
        next.buildings[moving] = { ...next.buildings[moving], x: gx, y: gy };
        commit(next);
        setMoving(null);
        toast.success("Bangunan dipindahkan");
        return;
      }
      const midx = buildingIndexAt(gx, gy);
      if (midx >= 0) { setMoving(midx); setSel(null); }
      return;
    }
    if (mode === "rotate") {
      const ridx = buildingIndexAt(gx, gy);
      if (ridx >= 0) rotateBuildingAt(ridx);
      return;
    }
    if (mode === "build") {
      // Pindahkan bangunan terpilih
      if (moving !== null) {
        if (!ghost || !ghost.valid) { toast.error("Lahan tidak valid untuk dipindah ke sini"); return; }
        const next = { ...state, buildings: [...state.buildings] };
        next.buildings[moving] = { ...next.buildings[moving], x: gx, y: gy };
        commit(next);
        setMoving(null);
        toast.success("Bangunan dipindahkan");
        return;
      }
      // Letakkan bangunan baru
      if (tool) {
        if (gx < 0 || gy < 0 || gx + tool.w > GRID_SIZE || gy + tool.h > GRID_SIZE) return;
        if (occupied(state.buildings, gx, gy, tool.w, tool.h)) { toast.error("Lahan sudah terisi"); return; }
        const next = { ...state, buildings: [...state.buildings, { bid: tool.bid, x: gx, y: gy, ...(toolVid ? { vid: toolVid } : {}) }] };
        commit(next);
        return;
      }
      // Tanpa tool: klik bangunan → buka inspector
      const idx = buildingIndexAt(gx, gy);
      setSel(idx >= 0 ? idx : null);
    }
  }

  function onMove(e) {
    if (!engineRef.current) return;
    setHover(gridAt(e));
  }

  function onMouseDown(e) {
    movedRef.current = false;
    if (mode === "road" || mode === "water" || mode === "runway") {
      const [gx, gy] = gridAt(e);
      const kind = mode === "road" ? "roads" : mode === "water" ? "water" : "runways";
      // Seret SELALU menambah. Jalan BOLEH menimpa sungai (otomatis jembatan);
      // landasan TIDAK boleh menimpa air; sungai TIDAK boleh memutus jalan/landasan.
      if (kind === "runways" && (state.water || []).includes(`${gx},${gy}`)) {
        toast.error("Tidak bisa — landasan tidak boleh di atas air");
        return;
      }
      if (kind === "water" && ((state.roads || []).includes(`${gx},${gy}`) || (state.runways || []).includes(`${gx},${gy}`))) {
        toast.error("Tidak bisa — sudah ada jalan/jembatan/landasan di sini");
        return;
      }
      const action = "add";
      drawingRef.current = { action, kind, perp: null, x0: gx, y0: gy, gx, gy };
      undoRef.current.push(state);
      paintCell(gx, gy);
    } else {
      dragRef.current = { x: e.clientX, y: e.clientY };
    }
  }
  function onMouseMove(e) {
    const d = drawingRef.current;
    if (d && engineRef.current) {
      const [gx, gy] = gridAt(e);
      // Sungai tidak boleh memutus jalan/landasan; landasan tidak boleh di atas air
      if (d.kind === "water" && ((state.roads || []).includes(`${gx},${gy}`) || (state.runways || []).includes(`${gx},${gy}`))) return;
      if (d.kind === "runways" && (state.water || []).includes(`${gx},${gy}`)) return;
      // SNAP ke sumbu grid dominan dari titik awal — seretan menghasilkan pita
      // lurus sempurna searah diagonal isometrik (bukan zigzag). Belokan = stroke baru.
      let tgx = gx, tgy = gy;
      if (Math.abs(gx - d.x0) >= Math.abs(gy - d.y0)) tgy = d.y0;
      else tgx = d.x0;
      // sumbu lajur tegak lurus arah jalan, tetap selama stroke
      if (!d.perp) d.perp = (tgy === d.y0) ? [0, 1] : [1, 0];
      // interpolasi jalur lurus satu sumbu (selalu kontigu)
      let cx = d.gx, cy = d.gy, guard = 0;
      while ((cx !== tgx || cy !== tgy) && guard < 400) {
        if (cx !== tgx) cx += Math.sign(tgx - cx);
        else cy += Math.sign(tgy - cy);
        paintCell(cx, cy);
        guard++;
      }
      d.gx = tgx; d.gy = tgy;
      return;
    }
    if (dragRef.current && engineRef.current) {
      movedRef.current = true;
      engineRef.current.pan(e.clientX - dragRef.current.x, e.clientY - dragRef.current.y);
      dragRef.current = { x: e.clientX, y: e.clientY };
      doRender();
    }
  }
  function onMouseUp() {
    drawingRef.current = false;
    dragRef.current = null;
  }
  function onWheel(e) {
    engineRef.current?.zoomAt(e.deltaY < 0 ? 1.1 : 0.9);
    doRender();
  }

  function onContextMenu(e) {
    if (!state || !engineRef.current) return;
    if (mode === "road" || mode === "water" || mode === "runway") {
      e.preventDefault();
      const [gx, gy] = gridAt(e);
      const kind = mode === "road" ? "roads" : mode === "water" ? "water" : "runways";
      const key = `${gx},${gy}`;
      if ((state[kind] || []).includes(key)) {
        undoRef.current.push(state);
        setTile(kind, gx, gy, "remove");
      }
    }
  }

  function resetDraw() { drawingRef.current = false; dragRef.current = null; }

  return { onClick, onMove, onMouseDown, onMouseMove, onMouseUp, onWheel, onContextMenu, resetDraw };
}
